// TODO: convert these to ZOD objects ... Zobjects?

import { Observable } from 'rxjs';
import * as z from 'zod';
import { ZodType } from 'zod';

export enum DatiqRulesRelations {
    All = 'all',
    Some = 'some',
    None = 'none',
}

export enum DatiqRulesConditionOperators {
    And = 'and',
    Or = 'or',
    Not = 'not',
}

export interface IDatiqRulesRule {
    /**
     * The condition operator is how the conditions should be operated on. The values
     * are AND, OR, NOT which means the following:
     * AND - All conditions are met
     * OR - One or more conditions are met
     * NONE - None of the conditions are met
     * * Met in this case means the condition, when ran through an evaluator, return true
     */
    conditionOperator: DatiqRulesConditionOperators;
    /**
     * Various data objects that will be consumed by the registered condition operators
     * to evaluate to true or false meaning the met the criteria.
     */
    conditions: IDatiqRulesCondition[];
}

export interface IDatiqRulesCondition {
    /**
     * The type of condition. This type should match one of the
     * evaluators in the evaluator map in the register service.
     * If it doesn't it evaluates to the default rule behavior
     */
    type: unknown;
    /**
     * The key to tell the evaluator how to evaluatre this condition.
     * In most cases, this key is used to determine the method in the evaluator
     * to use to evaluate this condition
     */
    key: unknown;
    /**
     * The comparison between the values. This will be using the Is enum,
     * but could also be a custom comparison based on the condition type and
     * key
     */
    is: unknown;
    /**
     * The value to compare too. This will be very specific to the
     * comparison and method in the evaluator.
     */
    value: unknown;
}

export interface IDatiqRulesData<T> {
    /**
     * The extra data for this rule. This will be helpful
     * when we need to use extra data to fuel other actions.
     * For example, we can add the event we want to track event
     * and the analytics event and filter out the in valid actions
     * (the actions that don't fit) and have it there then do extra steps
     */
    data?: T;
    /**
     * After running the evaluators on all the conditions in all the rules
     * this will say whether this rule passed (all conditions/rules met) or
     * didn't (based off the criteria if the rules didn't pass)
     */
    passed?: boolean;
    /**
     * The relation between the rules. The values ALL, SOME, NONE which means the following:
     * ALL - All rules passed
     * SOME - One or more rules passed
     * NONE - None of the rules passed
     * If by the above criteria is valid, then it will determine if the rule passed
     */
    relation?: DatiqRulesRelations;
    /**
     * All the various rules to pass. If by the relations criteria they all are valid,
     * ideally the passed boolean should be set to true.
     */
    rules: IDatiqRulesRule[];
}

export interface IDatiqRulesServiceEvaluator {
    type: string;

    /**
     * Since some condition evaluations might require to call
     * asyncrounous or state driven observables, it should always
     * return an observable of the results so that the other evaluators
     * can handle this synchronously.
     *
     * @param condition : condition to evaluate
     * @returns an observable saying whether this condition is
     * met or not met
     */
    evaluate$(condition: IDatiqRulesCondition): Observable<boolean>;
}

export const IqRulesDataSchema = <TRulesData extends ZodType>(
    rulesData: TRulesData,
) =>
    z.object({
        relation: z.union([
            z.literal(DatiqRulesRelations.All),
            z.literal(DatiqRulesRelations.Some),
            z.literal(DatiqRulesRelations.None),
        ]).optional(),
        passed: z.boolean().optional(),
        rules: z.array(
            z.object({
                conditionOperator: z.union([
                    z.literal(DatiqRulesConditionOperators.And),
                    z.literal(DatiqRulesConditionOperators.Or),
                    z.literal(DatiqRulesConditionOperators.Not),
                ]),
                conditions: z.array(
                    z.object({
                        type: z.unknown(),
                        key: z.unknown(),
                        is: z.unknown(),
                        value: z.unknown(),
                    }),
                ),
            }),
        ),
        data: rulesData.optional(),
    });
export const DATIQ_RULE_CONDITION_NAME_SUFFIX: string = 'ConditionEvaluator';
export const datiqRuleConditionName: (type: string) => string = (
    type: string,
) => `${type}${DATIQ_RULE_CONDITION_NAME_SUFFIX}`;
export const DATIQ_RULE_CONDITION_NOT_FOUND_DEFAULT: boolean = false;
